/**
 * Language Helper Class
 * Provides translations for common survey text and associated country flags
 */

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  countryCode: string;
  flagUrl: string;
}

export interface SurveyTranslations {
  // Navigation
  next: string;
  previous: string;
  submit: string;
  cancel: string;
  confirm: string;
  continue: string;
  back: string;
  finish: string;
  save: string;
  close: string;

  // Status
  required: string;
  optional: string;
  loading: string;
  error: string;
  thankYou: string;
  pageOf: string; // e.g., "Page {current} of {total}"

  // Selection
  pleaseSelect: string;
  selectOption: string;
  selectAll: string;
  clearAll: string;
  other: string;

  // Multiple choice selection constraints
  selectExactly: string;
  selectUpTo: string;
  selectAtLeast: string;
  selectBetween: string;
  to: string; // Used for ranges like "5 to 10"
  option: string;
  options: string;

  // Validation messages
  pleaseSelectAtLeast: string;
  pleaseSelectNoMoreThan: string;
  pleaseSpecifyOther: string;

  // Submission messages
  submitting: string;
  submissionFailed: string;
  invalidEmail: string;
  pleaseSpecify: string;
  alreadyCompleted: string;
  duplicateSubmission: string;

  // Input placeholders
  enterYourAnswer: string;
  enterYourResponse: string;
  enterYourEmail: string;
}

export class LanguageHelper {
  private static readonly FLAG_CDN_BASE = 'https://flagcdn.com/w40';

  /**
   * Generic "Survey" label translations used for fallbacks and document titles
   */
  private static readonly SURVEY_LABELS: Record<string, string> = {
    en: 'Survey',
    es: 'Encuesta',
    fr: 'Sondage',
    de: 'Umfrage',
    it: 'Sondaggio',
    pt: 'Pesquisa',
    nl: 'Enquête',
    pl: 'Ankieta',
    ru: 'Опрос',
    ja: 'アンケート',
    zh: '调查',
    'zh-tw': '調查',
    ko: '설문조사',
    ar: 'استبيان',
    hi: 'सर्वेक्षण',
    tr: 'Anket',
    sv: 'Enkät',
    da: 'Spørgeskema',
    no: 'Spørreundersøkelse',
    fi: 'Kysely',
    el: 'Έρευνα',
    cs: 'Průzkum',
    hu: 'Felmérés',
    ro: 'Sondaj',
    uk: 'Опитування',
    th: 'แบบสำรวจ',
    vi: 'Khảo sát',
    id: 'Survei',
    ms: 'Tinjauan',
    he: 'סקר'
  };

  /**
   * Complete list of supported languages with their metadata
   */
  static readonly LANGUAGES: Language[] = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      countryCode: 'gb',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/gb.png`
    },
    {
      code: 'es',
      name: 'Spanish',
      nativeName: 'Español',
      countryCode: 'es',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/es.png`
    },
    {
      code: 'fr',
      name: 'French',
      nativeName: 'Français',
      countryCode: 'fr',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/fr.png`
    },
    {
      code: 'de',
      name: 'German',
      nativeName: 'Deutsch',
      countryCode: 'de',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/de.png`
    },
    {
      code: 'it',
      name: 'Italian',
      nativeName: 'Italiano',
      countryCode: 'it',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/it.png`
    },
    {
      code: 'pt',
      name: 'Portuguese',
      nativeName: 'Português',
      countryCode: 'pt',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/pt.png`
    },
    {
      code: 'nl',
      name: 'Dutch',
      nativeName: 'Nederlands',
      countryCode: 'nl',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/nl.png`
    },
    {
      code: 'pl',
      name: 'Polish',
      nativeName: 'Polski',
      countryCode: 'pl',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/pl.png`
    },
    {
      code: 'ru',
      name: 'Russian',
      nativeName: 'Русский',
      countryCode: 'ru',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/ru.png`
    },
    {
      code: 'ja',
      name: 'Japanese',
      nativeName: '日本語',
      countryCode: 'jp',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/jp.png`
    },
    {
      code: 'zh',
      name: 'Chinese (Simplified)',
      nativeName: '简体中文',
      countryCode: 'cn',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/cn.png`
    },
    {
      code: 'zh-TW',
      name: 'Chinese (Traditional)',
      nativeName: '繁體中文',
      countryCode: 'tw',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/tw.png`
    },
    {
      code: 'ko',
      name: 'Korean',
      nativeName: '한국어',
      countryCode: 'kr',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/kr.png`
    },
    {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      countryCode: 'sa',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/sa.png`
    },
    {
      code: 'hi',
      name: 'Hindi',
      nativeName: 'हिन्दी',
      countryCode: 'in',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/in.png`
    },
    {
      code: 'tr',
      name: 'Turkish',
      nativeName: 'Türkçe',
      countryCode: 'tr',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/tr.png`
    },
    {
      code: 'sv',
      name: 'Swedish',
      nativeName: 'Svenska',
      countryCode: 'se',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/se.png`
    },
    {
      code: 'da',
      name: 'Danish',
      nativeName: 'Dansk',
      countryCode: 'dk',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/dk.png`
    },
    {
      code: 'no',
      name: 'Norwegian',
      nativeName: 'Norsk',
      countryCode: 'no',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/no.png`
    },
    {
      code: 'fi',
      name: 'Finnish',
      nativeName: 'Suomi',
      countryCode: 'fi',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/fi.png`
    },
    {
      code: 'el',
      name: 'Greek',
      nativeName: 'Ελληνικά',
      countryCode: 'gr',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/gr.png`
    },
    {
      code: 'cs',
      name: 'Czech',
      nativeName: 'Čeština',
      countryCode: 'cz',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/cz.png`
    },
    {
      code: 'hu',
      name: 'Hungarian',
      nativeName: 'Magyar',
      countryCode: 'hu',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/hu.png`
    },
    {
      code: 'ro',
      name: 'Romanian',
      nativeName: 'Română',
      countryCode: 'ro',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/ro.png`
    },
    {
      code: 'uk',
      name: 'Ukrainian',
      nativeName: 'Українська',
      countryCode: 'ua',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/ua.png`
    },
    {
      code: 'th',
      name: 'Thai',
      nativeName: 'ไทย',
      countryCode: 'th',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/th.png`
    },
    {
      code: 'vi',
      name: 'Vietnamese',
      nativeName: 'Tiếng Việt',
      countryCode: 'vn',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/vn.png`
    },
    {
      code: 'id',
      name: 'Indonesian',
      nativeName: 'Bahasa Indonesia',
      countryCode: 'id',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/id.png`
    },
    {
      code: 'ms',
      name: 'Malay',
      nativeName: 'Bahasa Melayu',
      countryCode: 'my',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/my.png`
    },
    {
      code: 'he',
      name: 'Hebrew',
      nativeName: 'עברית',
      countryCode: 'il',
      flagUrl: `${LanguageHelper.FLAG_CDN_BASE}/il.png`
    }
  ];

  /**
   * Translations for common survey text across all supported languages
   */
  private static readonly TRANSLATIONS: Record<string, SurveyTranslations> = {
    en: {
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      required: 'This field is mandatory',
      optional: 'Optional',
      pleaseSelect: 'Please select',
      selectOption: 'Select an option',
      loading: 'Loading...',
      error: 'Error',
      thankYou: 'Thank you!',
      pageOf: 'Page {current} of {total}',
      selectAll: 'Select All',
      clearAll: 'Clear All',
      other: 'Other',
      cancel: 'Cancel',
      confirm: 'Confirm',
      continue: 'Continue',
      back: 'Back',
      finish: 'Finish',
      save: 'Save',
      close: 'Close',
      selectExactly: 'Please select exactly',
      selectUpTo: 'Select up to',
      selectAtLeast: 'Select at least',
      selectBetween: 'Select',
      to: 'to',
      option: 'option',
      options: 'options',
      pleaseSelectAtLeast: 'Please select at least',
      pleaseSelectNoMoreThan: 'Please select no more than',
      pleaseSpecifyOther: 'Please enter other value',
      submitting: 'Submitting...',
      submissionFailed: 'Failed to submit survey. Please try again.',
      invalidEmail: 'Please input a valid email address.',
      pleaseSpecify: 'Please specify',
      alreadyCompleted: 'You have already completed this survey. Thank you for your participation!',
      duplicateSubmission: 'This survey has already been submitted. Thank you for your participation!',
      enterYourAnswer: 'Enter your answer...',
      enterYourResponse: 'Enter your response...',
      enterYourEmail: 'Enter your email...'
    },
    es: {
      next: 'Siguiente',
      previous: 'Anterior',
      submit: 'Enviar',
      required: 'Este campo es obligatorio',
      optional: 'Opcional',
      pleaseSelect: 'Por favor seleccione',
      selectOption: 'Seleccione una opción',
      loading: 'Cargando...',
      error: 'Error',
      thankYou: '¡Gracias!',
      pageOf: 'Página {current} de {total}',
      selectAll: 'Seleccionar todo',
      clearAll: 'Borrar todo',
      other: 'Otro',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      continue: 'Continuar',
      back: 'Atrás',
      finish: 'Terminar',
      save: 'Guardar',
      close: 'Cerrar',
      selectExactly: 'Por favor seleccione exactamente',
      selectUpTo: 'Seleccione hasta',
      selectAtLeast: 'Seleccione al menos',
      selectBetween: 'Seleccione',
      to: 'a',
      option: 'opción',
      options: 'opciones',
      pleaseSelectAtLeast: 'Por favor seleccione al menos',
      pleaseSelectNoMoreThan: 'Por favor seleccione no más de',
      pleaseSpecifyOther: 'Por favor ingrese otro valor',
      submitting: 'Enviando...',
      submissionFailed: 'Error al enviar la encuesta. Por favor, inténtelo de nuevo.',
      invalidEmail: 'Por favor introduce una dirección de correo válida.',
      pleaseSpecify: 'Por favor especifique',
      alreadyCompleted: 'Ya has completado esta encuesta. ¡Gracias por tu participación!',
      duplicateSubmission: 'Esta encuesta ya ha sido enviada. ¡Gracias por tu participación!',
      enterYourAnswer: 'Ingrese su respuesta...',
      enterYourResponse: 'Ingrese su respuesta...',
      enterYourEmail: 'Ingrese su correo electrónico...'
    },
    fr: {
      next: 'Suivant',
      previous: 'Précédent',
      submit: 'Soumettre',
      required: 'Ce champ est obligatoire',
      optional: 'Optionnel',
      pleaseSelect: 'Veuillez sélectionner',
      selectOption: 'Sélectionnez une option',
      loading: 'Chargement...',
      error: 'Erreur',
      thankYou: 'Merci!',
      pageOf: 'Page {current} sur {total}',
      selectAll: 'Tout sélectionner',
      clearAll: 'Tout effacer',
      other: 'Autre',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      continue: 'Continuer',
      back: 'Retour',
      finish: 'Terminer',
      save: 'Enregistrer',
      close: 'Fermer',
      selectExactly: 'Veuillez sélectionner exactement',
      selectUpTo: 'Sélectionnez jusqu\'à',
      selectAtLeast: 'Sélectionnez au moins',
      selectBetween: 'Sélectionnez',
      to: 'à',
      option: 'option',
      options: 'options',
      pleaseSelectAtLeast: 'Veuillez sélectionner au moins',
      pleaseSelectNoMoreThan: 'Veuillez sélectionner au maximum',
      pleaseSpecifyOther: 'Veuillez entrer une autre valeur',
      submitting: 'Soumission en cours...',
      submissionFailed: 'Échec de la soumission du sondage. Veuillez réessayer.',
      invalidEmail: 'Veuillez saisir une adresse e-mail valide.',
      pleaseSpecify: 'Veuillez préciser',
      alreadyCompleted: 'Vous avez déjà complété ce sondage. Merci pour votre participation !',
      duplicateSubmission: 'Ce sondage a déjà été soumis. Merci pour votre participation !',
      enterYourAnswer: 'Entrez votre réponse...',
      enterYourResponse: 'Entrez votre réponse...',
      enterYourEmail: 'Entrez votre email...'
    },
    de: {
      next: 'Weiter',
      previous: 'Zurück',
      submit: 'Absenden',
      required: 'Dieses Feld ist verpflichtend',
      optional: 'Optional',
      pleaseSelect: 'Bitte auswählen',
      selectOption: 'Wählen Sie eine Option',
      loading: 'Laden...',
      error: 'Fehler',
      thankYou: 'Vielen Dank!',
      pageOf: 'Seite {current} von {total}',
      selectAll: 'Alle auswählen',
      clearAll: 'Alle löschen',
      other: 'Sonstiges',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      continue: 'Fortfahren',
      back: 'Zurück',
      finish: 'Beenden',
      save: 'Speichern',
      close: 'Schließen',
      selectExactly: 'Bitte wählen Sie genau',
      selectUpTo: 'Wählen Sie bis zu',
      selectAtLeast: 'Wählen Sie mindestens',
      selectBetween: 'Wählen Sie',
      to: 'bis',
      option: 'Option',
      options: 'Optionen',
      pleaseSelectAtLeast: 'Bitte wählen Sie mindestens',
      pleaseSelectNoMoreThan: 'Bitte wählen Sie nicht mehr als',
      pleaseSpecifyOther: 'Bitte geben Sie einen anderen Wert ein',
      submitting: 'Wird gesendet...',
      submissionFailed: 'Umfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      pleaseSpecify: 'Bitte angeben',
      alreadyCompleted: 'Sie haben diese Umfrage bereits abgeschlossen. Vielen Dank für Ihre Teilnahme!',
      duplicateSubmission: 'Diese Umfrage wurde bereits übermittelt. Vielen Dank für Ihre Teilnahme!',
      enterYourAnswer: 'Geben Sie Ihre Antwort ein...',
      enterYourResponse: 'Geben Sie Ihre Antwort ein...',
      enterYourEmail: 'Geben Sie Ihre E-Mail ein...'
    },
    it: {
      next: 'Avanti',
      previous: 'Indietro',
      submit: 'Invia',
      required: 'Questo campo è obbligatorio',
      optional: 'Opzionale',
      pleaseSelect: 'Si prega di selezionare',
      selectOption: 'Seleziona un\'opzione',
      loading: 'Caricamento...',
      error: 'Errore',
      thankYou: 'Grazie!',
      pageOf: 'Pagina {current} di {total}',
      selectAll: 'Seleziona tutto',
      clearAll: 'Cancella tutto',
      other: 'Altro',
      cancel: 'Annulla',
      confirm: 'Conferma',
      continue: 'Continua',
      back: 'Indietro',
      finish: 'Fine',
      save: 'Salva',
      close: 'Chiudi',
      selectExactly: 'Selezionare esattamente',
      selectUpTo: 'Selezionare fino a',
      selectAtLeast: 'Selezionare almeno',
      selectBetween: 'Selezionare',
      to: 'a',
      option: 'opzione',
      options: 'opzioni',
      pleaseSelectAtLeast: 'Selezionare almeno',
      pleaseSelectNoMoreThan: 'Selezionare non più di',
      pleaseSpecifyOther: 'Si prega di inserire un altro valore',
      submitting: 'Invio in corso...',
      submissionFailed: 'Invio del sondaggio non riuscito. Riprova.',
      invalidEmail: 'Inserisci un indirizzo email valido.',
      pleaseSpecify: 'Si prega di specificare',
      alreadyCompleted: 'Hai già completato questo sondaggio. Grazie per la tua partecipazione!',
      duplicateSubmission: 'Questo sondaggio è già stato inviato. Grazie per la tua partecipazione!',
      enterYourAnswer: 'Inserisci la tua risposta...',
      enterYourResponse: 'Inserisci la tua risposta...',
      enterYourEmail: 'Inserisci la tua email...'
    },
    pt: {
      next: 'Próximo',
      previous: 'Anterior',
      submit: 'Enviar',
      required: 'Obrigatório',
      optional: 'Opcional',
      pleaseSelect: 'Por favor selecione',
      selectOption: 'Selecione uma opção',
      loading: 'Carregando...',
      error: 'Erro',
      thankYou: 'Obrigado!',
      pageOf: 'Página {current} de {total}',
      selectAll: 'Selecionar tudo',
      clearAll: 'Limpar tudo',
      other: 'Outro',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      continue: 'Continuar',
      back: 'Voltar',
      finish: 'Concluir',
      save: 'Salvar',
      close: 'Fechar',
      selectExactly: 'Por favor selecione exatamente',
      selectUpTo: 'Selecione até',
      selectAtLeast: 'Selecione pelo menos',
      selectBetween: 'Selecione',
      to: 'a',
      option: 'opção',
      options: 'opções',
      pleaseSelectAtLeast: 'Por favor selecione pelo menos',
      pleaseSelectNoMoreThan: 'Por favor selecione não mais que',
      pleaseSpecifyOther: 'Por favor insira outro valor',
      submitting: 'Enviando...',
      submissionFailed: 'Falha ao enviar a pesquisa. Por favor, tente novamente.',
      invalidEmail: 'Por favor insira um endereço de e-mail válido.',
      pleaseSpecify: 'Por favor especifique',
      alreadyCompleted: 'Você já completou esta pesquisa. Obrigado pela sua participação!',
      duplicateSubmission: 'Esta pesquisa já foi enviada. Obrigado pela sua participação!',
      enterYourAnswer: 'Digite sua resposta...',
      enterYourResponse: 'Digite sua resposta...',
      enterYourEmail: 'Digite seu e-mail...'
    },
    nl: {
      next: 'Volgende',
      previous: 'Vorige',
      submit: 'Verzenden',
      required: 'Verplicht',
      optional: 'Optioneel',
      pleaseSelect: 'Selecteer alstublieft',
      selectOption: 'Selecteer een optie',
      loading: 'Laden...',
      error: 'Fout',
      thankYou: 'Bedankt!',
      pageOf: 'Pagina {current} van {total}',
      selectAll: 'Alles selecteren',
      clearAll: 'Alles wissen',
      other: 'Anders',
      cancel: 'Annuleren',
      confirm: 'Bevestigen',
      continue: 'Doorgaan',
      back: 'Terug',
      finish: 'Voltooien',
      save: 'Opslaan',
      close: 'Sluiten',
      selectExactly: 'Selecteer exact',
      selectUpTo: 'Selecteer tot',
      selectAtLeast: 'Selecteer minimaal',
      selectBetween: 'Selecteer',
      to: 'tot',
      option: 'optie',
      options: 'opties',
      pleaseSelectAtLeast: 'Selecteer alstublieft minimaal',
      pleaseSelectNoMoreThan: 'Selecteer alstublieft niet meer dan',
      pleaseSpecifyOther: 'Voer een andere waarde in',
      submitting: 'Verzenden...',
      submissionFailed: 'Enquête verzenden mislukt. Probeer het opnieuw.',
      invalidEmail: 'Voer een geldig e-mailadres in.',
      pleaseSpecify: 'Specificeer alstublieft',
      alreadyCompleted: 'U heeft deze enquête al voltooid. Bedankt voor uw deelname!',
      duplicateSubmission: 'Deze enquête is al ingediend. Bedankt voor uw deelname!',
      enterYourAnswer: 'Voer uw antwoord in...',
      enterYourResponse: 'Voer uw reactie in...',
      enterYourEmail: 'Voer uw e-mail in...'
    },
    pl: {
      next: 'Dalej',
      previous: 'Wstecz',
      submit: 'Wyślij',
      required: 'Wymagane',
      optional: 'Opcjonalne',
      pleaseSelect: 'Proszę wybrać',
      selectOption: 'Wybierz opcję',
      loading: 'Ładowanie...',
      error: 'Błąd',
      thankYou: 'Dziękujemy!',
      pageOf: 'Strona {current} z {total}',
      selectAll: 'Zaznacz wszystko',
      clearAll: 'Wyczyść wszystko',
      other: 'Inne',
      cancel: 'Anuluj',
      confirm: 'Potwierdź',
      continue: 'Kontynuuj',
      back: 'Wróć',
      finish: 'Zakończ',
      save: 'Zapisz',
      close: 'Zamknij',
      selectExactly: 'Proszę wybrać dokładnie',
      selectUpTo: 'Wybierz do',
      selectAtLeast: 'Wybierz co najmniej',
      selectBetween: 'Wybierz',
      to: 'do',
      option: 'opcja',
      options: 'opcje',
      pleaseSelectAtLeast: 'Proszę wybrać co najmniej',
      pleaseSelectNoMoreThan: 'Proszę wybrać nie więcej niż',
      pleaseSpecifyOther: 'Proszę wprowadzić inną wartość',
      submitting: 'Wysyłanie...',
      submissionFailed: 'Nie udało się wysłać ankiety. Proszę spróbować ponownie.',
      invalidEmail: 'Proszę wprowadzić prawidłowy adres e-mail.',
      pleaseSpecify: 'Proszę określić',
      alreadyCompleted: 'Wypełniłeś już tę ankietę. Dziękujemy za udział!',
      duplicateSubmission: 'Ta ankieta została już przesłana. Dziękujemy za udział!',
      enterYourAnswer: 'Wprowadź swoją odpowiedź...',
      enterYourResponse: 'Wprowadź swoją odpowiedź...',
      enterYourEmail: 'Wprowadź swój e-mail...'
    },
    ru: {
      next: 'Далее',
      previous: 'Назад',
      submit: 'Отправить',
      required: 'Обязательно',
      optional: 'Необязательно',
      pleaseSelect: 'Пожалуйста, выберите',
      selectOption: 'Выберите вариант',
      loading: 'Загрузка...',
      error: 'Ошибка',
      thankYou: 'Спасибо!',
      pageOf: 'Страница {current} из {total}',
      selectAll: 'Выбрать все',
      clearAll: 'Очистить все',
      other: 'Другое',
      cancel: 'Отмена',
      confirm: 'Подтвердить',
      continue: 'Продолжить',
      back: 'Назад',
      finish: 'Завершить',
      save: 'Сохранить',
      close: 'Закрыть',
      selectExactly: 'Пожалуйста, выберите точно',
      selectUpTo: 'Выберите до',
      selectAtLeast: 'Выберите как минимум',
      selectBetween: 'Выберите',
      to: 'до',
      option: 'вариант',
      options: 'варианты',
      pleaseSelectAtLeast: 'Пожалуйста, выберите как минимум',
      pleaseSelectNoMoreThan: 'Пожалуйста, выберите не более',
      pleaseSpecifyOther: 'Пожалуйста, введите другое значение',
      submitting: 'Отправка...',
      submissionFailed: 'Не удалось отправить опрос. Пожалуйста, попробуйте снова.',
      invalidEmail: 'Пожалуйста, введите действительный адрес электронной почты.',
      pleaseSpecify: 'Пожалуйста, укажите',
      alreadyCompleted: 'Вы уже завершили этот опрос. Спасибо за ваше участие!',
      duplicateSubmission: 'Этот опрос уже был отправлен. Спасибо за ваше участие!',
      enterYourAnswer: 'Введите ваш ответ...',
      enterYourResponse: 'Введите ваш ответ...',
      enterYourEmail: 'Введите ваш e-mail...'
    },
    ja: {
      next: '次へ',
      previous: '前へ',
      submit: '送信',
      required: '必須',
      optional: '任意',
      pleaseSelect: '選択してください',
      selectOption: 'オプションを選択',
      loading: '読み込み中...',
      error: 'エラー',
      thankYou: 'ありがとうございます！',
      pageOf: 'ページ {current} / {total}',
      selectAll: 'すべて選択',
      clearAll: 'すべてクリア',
      other: 'その他',
      cancel: 'キャンセル',
      confirm: '確認',
      continue: '続ける',
      back: '戻る',
      finish: '完了',
      save: '保存',
      close: '閉じる',
      selectExactly: '正確に選択してください',
      selectUpTo: '最大',
      selectAtLeast: '最小',
      selectBetween: '選択',
      to: 'から',
      option: 'オプション',
      options: 'オプション',
      pleaseSelectAtLeast: '最小限選択してください',
      pleaseSelectNoMoreThan: '最大限選択してください',
      pleaseSpecifyOther: '他の値を入力してください',
      submitting: '送信中...',
      submissionFailed: 'アンケートの送信に失敗しました。もう一度お試しください。',
      invalidEmail: '有効なメールアドレスを入力してください。',
      pleaseSpecify: '指定してください',
      alreadyCompleted: 'このアンケートはすでに完了しています。ご参加ありがとうございます！',
      duplicateSubmission: 'このアンケートはすでに送信されています。ご参加ありがとうございます！',
      enterYourAnswer: '回答を入力してください...',
      enterYourResponse: '回答を入力してください...',
      enterYourEmail: 'メールアドレスを入力してください...'
    },
    zh: {
      next: '下一步',
      previous: '上一步',
      submit: '提交',
      required: '必填',
      optional: '可选',
      pleaseSelect: '请选择',
      selectOption: '选择一个选项',
      loading: '加载中...',
      error: '错误',
      thankYou: '谢谢！',
      pageOf: '第 {current} 页，共 {total} 页',
      selectAll: '全选',
      clearAll: '清除全部',
      other: '其他',
      cancel: '取消',
      confirm: '确认',
      continue: '继续',
      back: '返回',
      finish: '完成',
      save: '保存',
      close: '关闭',
      selectExactly: '请准确选择',
      selectUpTo: '最多选择',
      selectAtLeast: '至少选择',
      selectBetween: '选择',
      to: '到',
      option: '选项',
      options: '选项',
      pleaseSelectAtLeast: '请至少选择',
      pleaseSelectNoMoreThan: '请选择不超过',
      pleaseSpecifyOther: '请输入其他值',
      submitting: '提交中...',
      submissionFailed: '提交调查失败。请重试。',
      invalidEmail: '请输入有效的电子邮件地址。',
      pleaseSpecify: '请指定',
      alreadyCompleted: '您已经完成了此调查。感谢您的参与！',
      duplicateSubmission: '此调查已经提交。感谢您的参与！',
      enterYourAnswer: '输入您的答案...',
      enterYourResponse: '输入您的回复...',
      enterYourEmail: '输入您的电子邮件...'
    },
    'zh-TW': {
      next: '下一步',
      previous: '上一步',
      submit: '提交',
      required: '必填',
      optional: '可選',
      pleaseSelect: '請選擇',
      selectOption: '選擇一個選項',
      loading: '載入中...',
      error: '錯誤',
      thankYou: '謝謝！',
      pageOf: '第 {current} 頁，共 {total} 頁',
      selectAll: '全選',
      clearAll: '清除全部',
      other: '其他',
      cancel: '取消',
      confirm: '確認',
      continue: '繼續',
      back: '返回',
      finish: '完成',
      save: '儲存',
      close: '關閉',
      selectExactly: '請準確選擇',
      selectUpTo: '最多選擇',
      selectAtLeast: '至少選擇',
      selectBetween: '選擇',
      to: '到',
      option: '選項',
      options: '選項',
      pleaseSelectAtLeast: '請至少選擇',
      pleaseSelectNoMoreThan: '請選擇不超過',
      pleaseSpecifyOther: '請輸入其他值',
      submitting: '提交中...',
      submissionFailed: '提交調查失敗。請重試。',
      invalidEmail: '請輸入有效的電子郵件地址。',
      pleaseSpecify: '請指定',
      alreadyCompleted: '您已經完成了此調查。感謝您的參與！',
      duplicateSubmission: '此調查已經提交。感謝您的參與！',
      enterYourAnswer: '輸入您的答案...',
      enterYourResponse: '輸入您的回覆...',
      enterYourEmail: '輸入您的電子郵件...'
    },
    ko: {
      next: '다음',
      previous: '이전',
      submit: '제출',
      required: '필수',
      optional: '선택',
      pleaseSelect: '선택해주세요',
      selectOption: '옵션을 선택하세요',
      loading: '로딩 중...',
      error: '오류',
      thankYou: '감사합니다!',
      pageOf: '{total} 페이지 중 {current} 페이지',
      selectAll: '모두 선택',
      clearAll: '모두 지우기',
      other: '기타',
      cancel: '취소',
      confirm: '확인',
      continue: '계속',
      back: '뒤로',
      finish: '완료',
      save: '저장',
      close: '닫기',
      selectExactly: '정확히 선택하세요',
      selectUpTo: '최대',
      selectAtLeast: '최소',
      selectBetween: '선택',
      to: '에서',
      option: '옵션',
      options: '옵션',
      pleaseSelectAtLeast: '최소 선택하세요',
      pleaseSelectNoMoreThan: '최대 선택하세요',
      pleaseSpecifyOther: '다른 값을 입력하세요',
      submitting: '제출 중...',
      submissionFailed: '설문 제출에 실패했습니다. 다시 시도해주세요.',
      invalidEmail: '유효한 이메일 주소를 입력하세요.',
      pleaseSpecify: '명시하세요',
      alreadyCompleted: '이미 이 설문을 완료하셨습니다. 참여해 주셔서 감사합니다!',
      duplicateSubmission: '이 설문은 이미 제출되었습니다. 참여해 주셔서 감사합니다!',
      enterYourAnswer: '답변을 입력하세요...',
      enterYourResponse: '응답을 입력하세요...',
      enterYourEmail: '이메일을 입력하세요...'
    },
    ar: {
      next: 'التالي',
      previous: 'السابق',
      submit: 'إرسال',
      required: 'مطلوب',
      optional: 'اختياري',
      pleaseSelect: 'يرجى الاختيار',
      selectOption: 'اختر خيارًا',
      loading: 'جاري التحميل...',
      error: 'خطأ',
      thankYou: 'شكراً لك!',
      pageOf: 'صفحة {current} من {total}',
      selectAll: 'تحديد الكل',
      clearAll: 'مسح الكل',
      other: 'أخرى',
      cancel: 'إلغاء',
      confirm: 'تأكيد',
      continue: 'متابعة',
      back: 'رجوع',
      finish: 'إنهاء',
      save: 'حفظ',
      close: 'إغلاق',
      selectExactly: 'يرجى اختيار بالضبط',
      selectUpTo: 'اختر حتى',
      selectAtLeast: 'اختر على الأقل',
      selectBetween: 'اختر',
      to: 'إلى',
      option: 'خيار',
      options: 'خيارات',
      pleaseSelectAtLeast: 'يرجى اختيار على الأقل',
      pleaseSelectNoMoreThan: 'يرجى عدم اختيار أكثر من',
      pleaseSpecifyOther: 'يرجى إدخال قيمة أخرى',
      submitting: 'جاري الإرسال...',
      submissionFailed: 'فشل إرسال الاستبيان. يرجى المحاولة مرة أخرى.',
      invalidEmail: 'يرجى إدخال عنوان بريد إلكتروني صحيح.',
      pleaseSpecify: 'يرجى التحديد',
      alreadyCompleted: 'لقد أكملت هذا الاستبيان بالفعل. شكراً لمشاركتك!',
      duplicateSubmission: 'تم إرسال هذا الاستبيان بالفعل. شكراً لمشاركتك!',
      enterYourAnswer: 'أدخل إجابتك...',
      enterYourResponse: 'أدخل ردك...',
      enterYourEmail: 'أدخل بريدك الإلكتروني...'
    },
    hi: {
      next: 'अगला',
      previous: 'पिछला',
      submit: 'जमा करें',
      required: 'आवश्यक',
      optional: 'वैकल्पिक',
      pleaseSelect: 'कृपया चुनें',
      selectOption: 'एक विकल्प चुनें',
      loading: 'लोड हो रहा है...',
      error: 'त्रुटि',
      thankYou: 'धन्यवाद!',
      pageOf: 'पृष्ठ {current} का {total}',
      selectAll: 'सभी का चयन करें',
      clearAll: 'सभी साफ़ करें',
      other: 'अन्य',
      cancel: 'रद्द करें',
      confirm: 'पुष्टि करें',
      continue: 'जारी रखें',
      back: 'वापस',
      finish: 'समाप्त करें',
      save: 'सहेजें',
      close: 'बंद करें',
      selectExactly: 'कृपया सही मात्रा में चुनें',
      selectUpTo: 'अधिकतम चुनें',
      selectAtLeast: 'कम से कम चुनें',
      selectBetween: 'चुनें',
      to: 'से',
      option: 'विकल्प',
      options: 'विकल्प',
      pleaseSelectAtLeast: 'कृपया कम से कम चुनें',
      pleaseSelectNoMoreThan: 'कृपया अधिक से अधिक चुनें',
      pleaseSpecifyOther: 'कृपया अन्य मान दर्ज करें',
      submitting: 'जमा हो रहा है...',
      submissionFailed: 'सर्वेक्षण जमा करने में विफल। कृपया पुन: प्रयास करें।',
      invalidEmail: 'कृपया मान्य ईमेल पता दर्ज करें।',
      pleaseSpecify: 'कृपया निर्दिष्ट करें',
      alreadyCompleted: 'आपने इस सर्वेक्षण को पहले ही पूरा कर लिया है। आपकी भागीदारी के लिए धन्यवाद!',
      duplicateSubmission: 'यह सर्वेक्षण पहले ही जमा किया जा चुका है। आपकी भागीदारी के लिए धन्यवाद!',
      enterYourAnswer: 'अपना उत्तर दर्ज करें...',
      enterYourResponse: 'अपनी प्रतिक्रिया दर्ज करें...',
      enterYourEmail: 'अपना ईमेल दर्ज करें...'
    },
    tr: {
      next: 'İleri',
      previous: 'Geri',
      submit: 'Gönder',
      required: 'Gerekli',
      optional: 'İsteğe bağlı',
      pleaseSelect: 'Lütfen seçin',
      selectOption: 'Bir seçenek seçin',
      loading: 'Yükleniyor...',
      error: 'Hata',
      thankYou: 'Teşekkürler!',
      pageOf: 'Sayfa {current} / {total}',
      selectAll: 'Tümünü seç',
      clearAll: 'Tümünü temizle',
      other: 'Diğer',
      cancel: 'İptal',
      confirm: 'Onayla',
      continue: 'Devam et',
      back: 'Geri',
      finish: 'Bitir',
      save: 'Kaydet',
      close: 'Kapat',
      selectExactly: 'Lütfen tam olarak seçin',
      selectUpTo: 'En fazla seç',
      selectAtLeast: 'En az seç',
      selectBetween: 'Seç',
      to: 'ile',
      option: 'seçenek',
      options: 'seçenekler',
      pleaseSelectAtLeast: 'Lütfen en az seçin',
      pleaseSelectNoMoreThan: 'Lütfen en fazla seçin',
      pleaseSpecifyOther: 'Lütfen başka bir değer girin',
      submitting: 'Gönderiliyor...',
      submissionFailed: 'Anket gönderilemedi. Lütfen tekrar deneyin.',
      invalidEmail: 'Lütfen geçerli bir e-posta adresi girin.',
      pleaseSpecify: 'Lütfen belirtin',
      alreadyCompleted: 'Bu anketi zaten tamamladınız. Katılımınız için teşekkürler!',
      duplicateSubmission: 'Bu anket zaten gönderildi. Katılımınız için teşekkürler!',
      enterYourAnswer: 'Cevabınızı girin...',
      enterYourResponse: 'Yanıtınızı girin...',
      enterYourEmail: 'E-postanızı girin...'
    },
    sv: {
      next: 'Nästa',
      previous: 'Föregående',
      submit: 'Skicka',
      required: 'Obligatorisk',
      optional: 'Valfri',
      pleaseSelect: 'Vänligen välj',
      selectOption: 'Välj ett alternativ',
      loading: 'Laddar...',
      error: 'Fel',
      thankYou: 'Tack!',
      pageOf: 'Sida {current} av {total}',
      selectAll: 'Välj alla',
      clearAll: 'Rensa alla',
      other: 'Annat',
      cancel: 'Avbryt',
      confirm: 'Bekräfta',
      continue: 'Fortsätt',
      back: 'Tillbaka',
      finish: 'Slutför',
      save: 'Spara',
      close: 'Stäng',
      selectExactly: 'Vänligen välj exakt',
      selectUpTo: 'Välj upp till',
      selectAtLeast: 'Välj minst',
      selectBetween: 'Välj',
      to: 'till',
      option: 'alternativ',
      options: 'alternativ',
      pleaseSelectAtLeast: 'Vänligen välj minst',
      pleaseSelectNoMoreThan: 'Vänligen välj inte fler än',
      pleaseSpecifyOther: 'Vänligen ange ett annat värde',
      submitting: 'Skickar...',
      submissionFailed: 'Misslyckades att skicka enkäten. Försök igen.',
      invalidEmail: 'Vänligen ange en giltig e-postadress.',
      pleaseSpecify: 'Vänligen specificera',
      alreadyCompleted: 'Du har redan slutfört denna enkät. Tack för ditt deltagande!',
      duplicateSubmission: 'Denna enkät har redan skickats. Tack för ditt deltagande!',
      enterYourAnswer: 'Ange ditt svar...',
      enterYourResponse: 'Ange ditt svar...',
      enterYourEmail: 'Ange din e-postadress...'
    },
    da: {
      next: 'Næste',
      previous: 'Forrige',
      submit: 'Indsend',
      required: 'Påkrævet',
      optional: 'Valgfri',
      pleaseSelect: 'Vælg venligst',
      selectOption: 'Vælg en mulighed',
      loading: 'Indlæser...',
      error: 'Fejl',
      thankYou: 'Tak!',
      pageOf: 'Side {current} af {total}',
      selectAll: 'Vælg alle',
      clearAll: 'Ryd alle',
      other: 'Andet',
      cancel: 'Annuller',
      confirm: 'Bekræft',
      continue: 'Fortsæt',
      back: 'Tilbage',
      finish: 'Afslut',
      save: 'Gem',
      close: 'Luk',
      selectExactly: 'Vælg venligst nøjagtigt',
      selectUpTo: 'Vælg op til',
      selectAtLeast: 'Vælg mindst',
      selectBetween: 'Vælg',
      to: 'til',
      option: 'valgmulighed',
      options: 'valgmuligheder',
      pleaseSelectAtLeast: 'Vælg venligst mindst',
      pleaseSelectNoMoreThan: 'Vælg venligst ikke mere end',
      pleaseSpecifyOther: 'Angiv venligst en anden værdi',
      submitting: 'Indsender...',
      submissionFailed: 'Kunne ikke indsende spørgeskema. Prøv venligst igen.',
      invalidEmail: 'Angiv venligst en gyldig e-mailadresse.',
      pleaseSpecify: 'Angiv venligst',
      alreadyCompleted: 'Du har allerede udfyldt dette spørgeskema. Tak for din deltagelse!',
      duplicateSubmission: 'Dette spørgeskema er allerede indsendt. Tak for din deltagelse!',
      enterYourAnswer: 'Indtast dit svar...',
      enterYourResponse: 'Indtast dit svar...',
      enterYourEmail: 'Indtast din e-mail...'
    },
    no: {
      next: 'Neste',
      previous: 'Forrige',
      submit: 'Send inn',
      required: 'Påkrevd',
      optional: 'Valgfri',
      pleaseSelect: 'Vennligst velg',
      selectOption: 'Velg et alternativ',
      loading: 'Laster...',
      error: 'Feil',
      thankYou: 'Takk!',
      pageOf: 'Side {current} av {total}',
      selectAll: 'Velg alle',
      clearAll: 'Fjern alle',
      other: 'Annet',
      cancel: 'Avbryt',
      confirm: 'Bekreft',
      continue: 'Fortsett',
      back: 'Tilbake',
      finish: 'Fullfør',
      save: 'Lagre',
      close: 'Lukk',
      selectExactly: 'Vennligst velg nøyaktig',
      selectUpTo: 'Velg opp til',
      selectAtLeast: 'Velg minst',
      selectBetween: 'Velg',
      to: 'til',
      option: 'alternativ',
      options: 'alternativer',
      pleaseSelectAtLeast: 'Vennligst velg minst',
      pleaseSelectNoMoreThan: 'Vennligst velg ikke mer enn',
      pleaseSpecifyOther: 'Vennligst angi en annen verdi',
      submitting: 'Sender inn...',
      submissionFailed: 'Kunne ikke sende inn undersøkelsen. Vennligst prøv igjen.',
      invalidEmail: 'Vennligst oppgi en gyldig e-postadresse.',
      pleaseSpecify: 'Vennligst spesifiser',
      alreadyCompleted: 'Du har allerede fullført denne undersøkelsen. Takk for din deltakelse!',
      duplicateSubmission: 'Denne undersøkelsen er allerede sendt inn. Takk for din deltakelse!',
      enterYourAnswer: 'Skriv inn ditt svar...',
      enterYourResponse: 'Skriv inn ditt svar...',
      enterYourEmail: 'Skriv inn din e-post...'
    },
    fi: {
      next: 'Seuraava',
      previous: 'Edellinen',
      submit: 'Lähetä',
      required: 'Pakollinen',
      optional: 'Valinnainen',
      pleaseSelect: 'Ole hyvä ja valitse',
      selectOption: 'Valitse vaihtoehto',
      loading: 'Ladataan...',
      error: 'Virhe',
      thankYou: 'Kiitos!',
      pageOf: 'Sivu {current} / {total}',
      selectAll: 'Valitse kaikki',
      clearAll: 'Tyhjennä kaikki',
      other: 'Muu',
      cancel: 'Peruuta',
      confirm: 'Vahvista',
      continue: 'Jatka',
      back: 'Takaisin',
      finish: 'Valmis',
      save: 'Tallenna',
      close: 'Sulje',
      selectExactly: 'Ole hyvä ja valitse tarkalleen',
      selectUpTo: 'Valitse enintään',
      selectAtLeast: 'Valitse vähintään',
      selectBetween: 'Valitse',
      to: 'ja',
      option: 'vaihtoehto',
      options: 'vaihtoehdot',
      pleaseSelectAtLeast: 'Ole hyvä ja valitse vähintään',
      pleaseSelectNoMoreThan: 'Ole hyvä ja valitse enintään',
      pleaseSpecifyOther: 'Ole hyvä ja anna toinen arvo',
      submitting: 'Lähetetään...',
      submissionFailed: 'Kyselyn lähettäminen epäonnistui. Ole hyvä ja yritä uudelleen.',
      invalidEmail: 'Ole hyvä ja anna kelvollinen sähköpostiosoite.',
      pleaseSpecify: 'Ole hyvä ja täsmennä',
      alreadyCompleted: 'Olet jo täyttänyt tämän kyselyn. Kiitos osallistumisestasi!',
      duplicateSubmission: 'Tämä kysely on jo lähetetty. Kiitos osallistumisestasi!',
      enterYourAnswer: 'Syötä vastauksesi...',
      enterYourResponse: 'Syötä vastauksesi...',
      enterYourEmail: 'Syötä sähköpostisi...'
    },
    el: {
      next: 'Επόμενο',
      previous: 'Προηγούμενο',
      submit: 'Υποβολή',
      required: 'Υποχρεωτικό',
      optional: 'Προαιρετικό',
      pleaseSelect: 'Παρακαλώ επιλέξτε',
      selectOption: 'Επιλέξτε μια επιλογή',
      loading: 'Φόρτωση...',
      error: 'Σφάλμα',
      thankYou: 'Ευχαριστούμε!',
      pageOf: 'Σελίδα {current} από {total}',
      selectAll: 'Επιλογή όλων',
      clearAll: 'Εκκαθάριση όλων',
      other: 'Άλλο',
      cancel: 'Ακύρωση',
      confirm: 'Επιβεβαίωση',
      continue: 'Συνέχεια',
      back: 'Πίσω',
      finish: 'Τέλος',
      save: 'Αποθήκευση',
      close: 'Κλείσιμο',
      selectExactly: 'Παρακαλώ επιλέξτε ακριβώς',
      selectUpTo: 'Επιλέξτε έως',
      selectAtLeast: 'Επιλέξτε τουλάχιστον',
      selectBetween: 'Επιλέξτε',
      to: 'έως',
      option: 'επιλογή',
      options: 'επιλογές',
      pleaseSelectAtLeast: 'Παρακαλώ επιλέξτε τουλάχιστον',
      pleaseSelectNoMoreThan: 'Παρακαλώ επιλέξτε όχι περισσότερα από',
      pleaseSpecifyOther: 'Παρακαλώ εισάγετε άλλη τιμή',
      submitting: 'Υποβολή...',
      submissionFailed: 'Αποτυχία υποβολής ερωτηματολογίου. Παρακαλώ δοκιμάστε ξανά.',
      invalidEmail: 'Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email.',
      pleaseSpecify: 'Παρακαλώ διευκρινίστε',
      alreadyCompleted: 'Έχετε ήδη ολοκληρώσει αυτό το ερωτηματολόγιο. Ευχαριστούμε για τη συμμετοχή σας!',
      duplicateSubmission: 'Αυτό το ερωτηματολόγιο έχει ήδη υποβληθεί. Ευχαριστούμε για τη συμμετοχή σας!',
      enterYourAnswer: 'Εισάγετε την απάντησή σας...',
      enterYourResponse: 'Εισάγετε την απάντησή σας...',
      enterYourEmail: 'Εισάγετε το email σας...'
    },
    cs: {
      next: 'Další',
      previous: 'Předchozí',
      submit: 'Odeslat',
      required: 'Povinné',
      optional: 'Volitelné',
      pleaseSelect: 'Prosím vyberte',
      selectOption: 'Vyberte možnost',
      loading: 'Načítání...',
      error: 'Chyba',
      thankYou: 'Děkujeme!',
      pageOf: 'Stránka {current} z {total}',
      selectAll: 'Vybrat vše',
      clearAll: 'Vymazat vše',
      other: 'Jiné',
      cancel: 'Zrušit',
      confirm: 'Potvrdit',
      continue: 'Pokračovat',
      back: 'Zpět',
      finish: 'Dokončit',
      save: 'Uložit',
      close: 'Zavřít',
      selectExactly: 'Prosím vyberte přesně',
      selectUpTo: 'Vyberte až',
      selectAtLeast: 'Vyberte alespoň',
      selectBetween: 'Vyberte',
      to: 'až',
      option: 'možnost',
      options: 'možnosti',
      pleaseSelectAtLeast: 'Prosím vyberte alespoň',
      pleaseSelectNoMoreThan: 'Prosím vyberte ne více než',
      pleaseSpecifyOther: 'Prosím zadejte jinou hodnotu',
      submitting: 'Odesílání...',
      submissionFailed: 'Nepodařilo se odeslat průzkum. Zkuste to prosím znovu.',
      invalidEmail: 'Prosím zadejte platnou e-mailovou adresu.',
      pleaseSpecify: 'Prosím upřesněte',
      alreadyCompleted: 'Tento průzkum jste již dokončili. Děkujeme za vaši účast!',
      duplicateSubmission: 'Tento průzkum již byl odeslán. Děkujeme za vaši účast!',
      enterYourAnswer: 'Zadejte svou odpověď...',
      enterYourResponse: 'Zadejte svou odpověď...',
      enterYourEmail: 'Zadejte svůj e-mail...'
    },
    hu: {
      next: 'Következő',
      previous: 'Előző',
      submit: 'Küldés',
      required: 'Kötelező',
      optional: 'Opcionális',
      pleaseSelect: 'Kérjük, válasszon',
      selectOption: 'Válasszon egy lehetőséget',
      loading: 'Betöltés...',
      error: 'Hiba',
      thankYou: 'Köszönjük!',
      pageOf: '{current}. oldal a(z) {total} -ból',
      selectAll: 'Összes kiválasztása',
      clearAll: 'Összes törlése',
      other: 'Egyéb',
      cancel: 'Mégse',
      confirm: 'Megerősítés',
      continue: 'Folytatás',
      back: 'Vissza',
      finish: 'Befejezés',
      save: 'Mentés',
      close: 'Bezárás',
      selectExactly: 'Kérjük, válasszon pontosan',
      selectUpTo: 'Válasszon legfeljebb',
      selectAtLeast: 'Válasszon legalább',
      selectBetween: 'Válasszon',
      to: 'és',
      option: 'lehetőség',
      options: 'lehetőségek',
      pleaseSelectAtLeast: 'Kérjük, válasszon legalább',
      pleaseSelectNoMoreThan: 'Kérjük, ne válasszon többet, mint',
      pleaseSpecifyOther: 'Kérjük, adjon meg másik értéket',
      submitting: 'Küldés...',
      submissionFailed: 'Nem sikerült elküldeni a felmérést. Kérjük, próbálja újra.',
      invalidEmail: 'Kérjük, adjon meg érvényes e-mail címet.',
      pleaseSpecify: 'Kérjük, pontosítsa',
      alreadyCompleted: 'Már kitöltötte ezt a felmérést. Köszönjük a részvételt!',
      duplicateSubmission: 'Ez a felmérés már elküldésre került. Köszönjük a részvételt!',
      enterYourAnswer: 'Írja be válaszát...',
      enterYourResponse: 'Írja be válaszát...',
      enterYourEmail: 'Írja be e-mail címét...'
    },
    ro: {
      next: 'Următorul',
      previous: 'Anterior',
      submit: 'Trimite',
      required: 'Obligatoriu',
      optional: 'Opțional',
      pleaseSelect: 'Vă rugăm selectați',
      selectOption: 'Selectați o opțiune',
      loading: 'Se încarcă...',
      error: 'Eroare',
      thankYou: 'Mulțumim!',
      pageOf: 'Pagina {current} din {total}',
      selectAll: 'Selectează tot',
      clearAll: 'Șterge tot',
      other: 'Altele',
      cancel: 'Anulează',
      confirm: 'Confirmă',
      continue: 'Continuă',
      back: 'Înapoi',
      finish: 'Finalizează',
      save: 'Salvează',
      close: 'Închide',
      selectExactly: 'Vă rugăm selectați exact',
      selectUpTo: 'Selectați până la',
      selectAtLeast: 'Selectați cel puțin',
      selectBetween: 'Selectați',
      to: 'până la',
      option: 'opțiune',
      options: 'opțiuni',
      pleaseSelectAtLeast: 'Vă rugăm selectați cel puțin',
      pleaseSelectNoMoreThan: 'Vă rugăm selectați nu mai mult de',
      pleaseSpecifyOther: 'Vă rugăm introduceți altă valoare',
      submitting: 'Se trimite...',
      submissionFailed: 'Nu s-a putut trimite sondajul. Vă rugăm încercați din nou.',
      invalidEmail: 'Vă rugăm introduceți o adresă de email validă.',
      pleaseSpecify: 'Vă rugăm specificați',
      alreadyCompleted: 'Ați completat deja acest sondaj. Vă mulțumim pentru participare!',
      duplicateSubmission: 'Acest sondaj a fost deja trimis. Vă mulțumim pentru participare!',
      enterYourAnswer: 'Introduceți răspunsul dvs...',
      enterYourResponse: 'Introduceți răspunsul dvs...',
      enterYourEmail: 'Introduceți email-ul dvs...'
    },
    uk: {
      next: 'Далі',
      previous: 'Назад',
      submit: 'Надіслати',
      required: "Обов'язково",
      optional: "Необов'язково",
      pleaseSelect: 'Будь ласка, виберіть',
      selectOption: 'Виберіть варіант',
      loading: 'Завантаження...',
      error: 'Помилка',
      thankYou: 'Дякуємо!',
      pageOf: 'Сторінка {current} з {total}',
      selectAll: 'Вибрати все',
      clearAll: 'Очистити все',
      other: 'Інше',
      cancel: 'Скасувати',
      confirm: 'Підтвердити',
      continue: 'Продовжити',
      back: 'Назад',
      finish: 'Завершити',
      save: 'Зберегти',
      close: 'Закрити',
      selectExactly: 'Будь ласка, виберіть точно',
      selectUpTo: 'Виберіть до',
      selectAtLeast: 'Виберіть принаймні',
      selectBetween: 'Виберіть',
      to: 'до',
      option: 'варіант',
      options: 'варіанти',
      pleaseSelectAtLeast: 'Будь ласка, виберіть принаймні',
      pleaseSelectNoMoreThan: 'Будь ласка, виберіть не більше ніж',
      pleaseSpecifyOther: 'Будь ласка, введіть інше значення',
      submitting: 'Надсилання...',
      submissionFailed: 'Не вдалося надіслати опитування. Будь ласка, спробуйте знову.',
      invalidEmail: 'Будь ласка, введіть дійсну адресу електронної пошти.',
      pleaseSpecify: 'Будь ласка, вкажіть',
      alreadyCompleted: 'Ви вже завершили це опитування. Дякуємо за вашу участь!',
      duplicateSubmission: 'Це опитування вже було надіслано. Дякуємо за вашу участь!',
      enterYourAnswer: 'Введіть вашу відповідь...',
      enterYourResponse: 'Введіть вашу відповідь...',
      enterYourEmail: 'Введіть вашу електронну пошту...'
    },
    th: {
      next: 'ถัดไป',
      previous: 'ก่อนหน้า',
      submit: 'ส่ง',
      required: 'จำเป็น',
      optional: 'ไม่จำเป็น',
      pleaseSelect: 'กรุณาเลือก',
      selectOption: 'เลือกตัวเลือก',
      loading: 'กำลังโหลด...',
      error: 'ข้อผิดพลาด',
      thankYou: 'ขอบคุณ!',
      pageOf: 'หน้า {current} จาก {total}',
      selectAll: 'เลือกทั้งหมด',
      clearAll: 'ล้างทั้งหมด',
      other: 'อื่นๆ',
      cancel: 'ยกเลิก',
      confirm: 'ยืนยัน',
      continue: 'ดำเนินการต่อ',
      back: 'ย้อนกลับ',
      finish: 'เสร็จสิ้น',
      save: 'บันทึก',
      close: 'ปิด',
      selectExactly: 'กรุณาเลือกตรงตาม',
      selectUpTo: 'เลือกได้สูงสุด',
      selectAtLeast: 'เลือกอย่างน้อย',
      selectBetween: 'เลือก',
      to: 'ถึง',
      option: 'ตัวเลือก',
      options: 'ตัวเลือก',
      pleaseSelectAtLeast: 'กรุณาเลือกอย่างน้อย',
      pleaseSelectNoMoreThan: 'กรุณาเลือกไม่เกิน',
      pleaseSpecifyOther: 'กรุณาระบุค่าอื่น',
      submitting: 'กำลังส่ง...',
      submissionFailed: 'การส่งแบบสำรวจล้มเหลว กรุณาลองอีกครั้ง',
      invalidEmail: 'กรุณาป้อนที่อยู่อีเมลที่ถูกต้อง',
      pleaseSpecify: 'กรุณาระบุ',
      alreadyCompleted: 'คุณได้ทำแบบสำรวจนี้เรียบร้อยแล้ว ขอบคุณสำหรับการเข้าร่วม!',
      duplicateSubmission: 'แบบสำรวจนี้ถูกส่งแล้ว ขอบคุณสำหรับการเข้าร่วม!',
      enterYourAnswer: 'ป้อนคำตอบของคุณ...',
      enterYourResponse: 'ป้อนคำตอบของคุณ...',
      enterYourEmail: 'ป้อนอีเมลของคุณ...'
    },
    vi: {
      next: 'Tiếp theo',
      previous: 'Trước',
      submit: 'Gửi',
      required: 'Bắt buộc',
      optional: 'Tùy chọn',
      pleaseSelect: 'Vui lòng chọn',
      selectOption: 'Chọn một tùy chọn',
      loading: 'Đang tải...',
      error: 'Lỗi',
      thankYou: 'Cảm ơn!',
      pageOf: 'Trang {current} / {total}',
      selectAll: 'Chọn tất cả',
      clearAll: 'Xóa tất cả',
      other: 'Khác',
      cancel: 'Hủy',
      confirm: 'Xác nhận',
      continue: 'Tiếp tục',
      back: 'Quay lại',
      finish: 'Hoàn thành',
      save: 'Lưu',
      close: 'Đóng',
      selectExactly: 'Vui lòng chọn chính xác',
      selectUpTo: 'Chọn tối đa',
      selectAtLeast: 'Chọn ít nhất',
      selectBetween: 'Chọn',
      to: 'đến',
      option: 'tùy chọn',
      options: 'tùy chọn',
      pleaseSelectAtLeast: 'Vui lòng chọn ít nhất',
      pleaseSelectNoMoreThan: 'Vui lòng chọn không quá',
      pleaseSpecifyOther: 'Vui lòng nhập giá trị khác',
      submitting: 'Đang gửi...',
      submissionFailed: 'Không thể gửi khảo sát. Vui lòng thử lại.',
      invalidEmail: 'Vui lòng nhập địa chỉ email hợp lệ.',
      pleaseSpecify: 'Vui lòng chỉ rõ',
      alreadyCompleted: 'Bạn đã hoàn thành khảo sát này. Cảm ơn sự tham gia của bạn!',
      duplicateSubmission: 'Khảo sát này đã được gửi. Cảm ơn sự tham gia của bạn!',
      enterYourAnswer: 'Nhập câu trả lời của bạn...',
      enterYourResponse: 'Nhập phản hồi của bạn...',
      enterYourEmail: 'Nhập email của bạn...'
    },
    id: {
      next: 'Berikutnya',
      previous: 'Sebelumnya',
      submit: 'Kirim',
      required: 'Wajib',
      optional: 'Opsional',
      pleaseSelect: 'Silakan pilih',
      selectOption: 'Pilih opsi',
      loading: 'Memuat...',
      error: 'Kesalahan',
      thankYou: 'Terima kasih!',
      pageOf: 'Halaman {current} dari {total}',
      selectAll: 'Pilih semua',
      clearAll: 'Hapus semua',
      other: 'Lainnya',
      cancel: 'Batal',
      confirm: 'Konfirmasi',
      continue: 'Lanjutkan',
      back: 'Kembali',
      finish: 'Selesai',
      save: 'Simpan',
      close: 'Tutup',
      selectExactly: 'Silakan pilih tepat',
      selectUpTo: 'Pilih hingga',
      selectAtLeast: 'Pilih setidaknya',
      selectBetween: 'Pilih',
      to: 'hingga',
      option: 'pilihan',
      options: 'pilihan',
      pleaseSelectAtLeast: 'Silakan pilih setidaknya',
      pleaseSelectNoMoreThan: 'Silakan pilih tidak lebih dari',
      pleaseSpecifyOther: 'Silakan masukkan nilai lain',
      submitting: 'Mengirim...',
      submissionFailed: 'Gagal mengirim survei. Silakan coba lagi.',
      invalidEmail: 'Silakan masukkan alamat email yang valid.',
      pleaseSpecify: 'Silakan spesifikasikan',
      alreadyCompleted: 'Anda telah menyelesaikan survei ini. Terima kasih atas partisipasi Anda!',
      duplicateSubmission: 'Survei ini telah dikirim. Terima kasih atas partisipasi Anda!',
      enterYourAnswer: 'Masukkan jawaban Anda...',
      enterYourResponse: 'Masukkan respons Anda...',
      enterYourEmail: 'Masukkan email Anda...'
    },
    ms: {
      next: 'Seterusnya',
      previous: 'Sebelumnya',
      submit: 'Hantar',
      required: 'Diperlukan',
      optional: 'Pilihan',
      pleaseSelect: 'Sila pilih',
      selectOption: 'Pilih pilihan',
      loading: 'Memuatkan...',
      error: 'Ralat',
      thankYou: 'Terima kasih!',
      pageOf: 'Halaman {current} daripada {total}',
      selectAll: 'Pilih semua',
      clearAll: 'Kosongkan semua',
      other: 'Lain-lain',
      cancel: 'Batal',
      confirm: 'Sahkan',
      continue: 'Teruskan',
      back: 'Kembali',
      finish: 'Selesai',
      save: 'Simpan',
      close: 'Tutup',
      selectExactly: 'Sila pilih tepat',
      selectUpTo: 'Pilih sehingga',
      selectAtLeast: 'Pilih sekurang-kurangnya',
      selectBetween: 'Pilih',
      to: 'hingga',
      option: 'pilihan',
      options: 'pilihan',
      pleaseSelectAtLeast: 'Sila pilih sekurang-kurangnya',
      pleaseSelectNoMoreThan: 'Sila pilih tidak lebih daripada',
      pleaseSpecifyOther: 'Sila masukkan nilai lain',
      submitting: 'Menghantar...',
      submissionFailed: 'Gagal menghantar tinjauan. Sila cuba lagi.',
      invalidEmail: 'Sila masukkan alamat e-mel yang sah.',
      pleaseSpecify: 'Sila nyatakan',
      alreadyCompleted: 'Anda telah melengkapkan tinjauan ini. Terima kasih atas penyertaan anda!',
      duplicateSubmission: 'Tinjauan ini telah dihantar. Terima kasih atas penyertaan anda!',
      enterYourAnswer: 'Masukkan jawapan anda...',
      enterYourResponse: 'Masukkan respons anda...',
      enterYourEmail: 'Masukkan e-mel anda...'
    },
    he: {
      next: 'הבא',
      previous: 'הקודם',
      submit: 'שלח',
      required: 'חובה',
      optional: 'אופציונלי',
      pleaseSelect: 'אנא בחר',
      selectOption: 'בחר אפשרות',
      loading: 'טוען...',
      error: 'שגיאה',
      thankYou: 'תודה!',
      pageOf: 'עמוד {current} מתוך {total}',
      selectAll: 'בחר הכל',
      clearAll: 'נקה הכל',
      other: 'אחר',
      cancel: 'ביטול',
      confirm: 'אישור',
      continue: 'המשך',
      back: 'חזור',
      finish: 'סיום',
      save: 'שמור',
      close: 'סגור',
      selectExactly: 'אנא בחר בדיוק',
      selectUpTo: 'בחר עד',
      selectAtLeast: 'בחר לפחות',
      selectBetween: 'בחר',
      to: 'עד',
      option: 'אפשרות',
      options: 'אפשרויות',
      pleaseSelectAtLeast: 'אנא בחר לפחות',
      pleaseSelectNoMoreThan: 'אנא בחר לא יותר מ',
      pleaseSpecifyOther: 'אנא הזן ערך אחר',
      submitting: 'שולח...',
      submissionFailed: 'נכשל בשליחת הסקר. אנא נסה שוב.',
      invalidEmail: 'אנא הזן כתובת אימייל תקינה.',
      pleaseSpecify: 'אנא ציין',
      alreadyCompleted: 'כבר השלמת את הסקר הזה. תודה על השתתפותך!',
      duplicateSubmission: 'סקר זה כבר נשלח. תודה על השתתפותך!',
      enterYourAnswer: 'הזן את תשובתך...',
      enterYourResponse: 'הזן את תגובתך...',
      enterYourEmail: 'הזן את האימייל שלך...'
    },
    tl: {
      next: 'Susunod',
      previous: 'Nakaraan',
      submit: 'Isumite',
      required: 'Kinakailangan',
      optional: 'Opsyonal',
      pleaseSelect: 'Mangyaring pumili',
      selectOption: 'Pumili ng opsyon',
      loading: 'Naglo-load...',
      error: 'Error',
      thankYou: 'Salamat!',
      pageOf: 'Pahina {current} ng {total}',
      selectAll: 'Piliin lahat',
      clearAll: 'I-clear lahat',
      other: 'Iba',
      cancel: 'Kanselahin',
      confirm: 'Kumpirmahin',
      continue: 'Magpatuloy',
      back: 'Bumalik',
      finish: 'Tapusin',
      save: 'I-save',
      close: 'Isara',
      selectExactly: 'Mangyaring pumili ng eksaktong',
      selectUpTo: 'Pumili hanggang',
      selectAtLeast: 'Pumili ng hindi bababa sa',
      selectBetween: 'Pumili',
      to: 'hanggang',
      option: 'opsyon',
      options: 'mga opsyon',
      pleaseSelectAtLeast: 'Mangyaring pumili ng hindi bababa sa',
      pleaseSelectNoMoreThan: 'Mangyaring pumili ng hindi hihigit sa',
      pleaseSpecifyOther: 'Mangyaring maglagay ng ibang halaga',
      submitting: 'Isinusumite...',
      submissionFailed: 'Nabigo ang pagsusumite ng survey. Mangyaring subukan muli.',
      invalidEmail: 'Mangyaring maglagay ng wastong email address.',
      pleaseSpecify: 'Mangyaring tukuyin',
      alreadyCompleted: 'Natapos mo na ang survey na ito. Salamat sa iyong pakikilahok!',
      duplicateSubmission: 'Ang survey na ito ay naisumite na. Salamat sa iyong pakikilahok!',
      enterYourAnswer: 'Ilagay ang iyong sagot...',
      enterYourResponse: 'Ilagay ang iyong tugon...',
      enterYourEmail: 'Ilagay ang iyong email...'
    },
    bn: {
      next: 'পরবর্তী',
      previous: 'পূর্ববর্তী',
      submit: 'জমা দিন',
      required: 'প্রয়োজনীয়',
      optional: 'ঐচ্ছিক',
      pleaseSelect: 'অনুগ্রহ করে নির্বাচন করুন',
      selectOption: 'একটি বিকল্প নির্বাচন করুন',
      loading: 'লোড হচ্ছে...',
      error: 'ত্রুটি',
      thankYou: 'ধন্যবাদ!',
      pageOf: 'পৃষ্ঠা {current} এর {total}',
      selectAll: 'সব নির্বাচন করুন',
      clearAll: 'সব মুছুন',
      other: 'অন্যান্য',
      cancel: 'বাতিল করুন',
      confirm: 'নিশ্চিত করুন',
      continue: 'চালিয়ে যান',
      back: 'ফিরে যান',
      finish: 'শেষ করুন',
      save: 'সংরক্ষণ করুন',
      close: 'বন্ধ করুন',
      selectExactly: 'অনুগ্রহ করে ঠিক নির্বাচন করুন',
      selectUpTo: 'পর্যন্ত নির্বাচন করুন',
      selectAtLeast: 'অন্তত নির্বাচন করুন',
      selectBetween: 'নির্বাচন করুন',
      to: 'থেকে',
      option: 'বিকল্প',
      options: 'বিকল্পগুলি',
      pleaseSelectAtLeast: 'অনুগ্রহ করে অন্তত নির্বাচন করুন',
      pleaseSelectNoMoreThan: 'অনুগ্রহ করে এর বেশি নির্বাচন করবেন না',
      pleaseSpecifyOther: 'অনুগ্রহ করে অন্য মান লিখুন',
      submitting: 'জমা দেওয়া হচ্ছে...',
      submissionFailed: 'সমীক্ষা জমা দিতে ব্যর্থ হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
      invalidEmail: 'অনুগ্রহ করে একটি বৈধ ইমেল ঠিকানা লিখুন।',
      pleaseSpecify: 'অনুগ্রহ করে উল্লেখ করুন',
      alreadyCompleted: 'আপনি ইতিমধ্যে এই সমীক্ষা সম্পন্ন করেছেন। আপনার অংশগ্রহণের জন্য ধন্যবাদ!',
      duplicateSubmission: 'এই সমীক্ষা ইতিমধ্যে জমা দেওয়া হয়েছে। আপনার অংশগ্রহণের জন্য ধন্যবাদ!',
      enterYourAnswer: 'আপনার উত্তর লিখুন...',
      enterYourResponse: 'আপনার প্রতিক্রিয়া লিখুন...',
      enterYourEmail: 'আপনার ইমেল লিখুন...'
    }
  };

  /**
   * Get all available languages
   */
  static getAllLanguages(): Language[] {
    return this.LANGUAGES;
  }

  /**
   * Get a specific language by its code
   */
  static getLanguage(code: string): Language | undefined {
    return this.LANGUAGES.find(lang => lang.code === code);
  }

  /**
   * Get translations for a specific language
   */
  static getTranslations(languageCode: string): SurveyTranslations {
    return this.TRANSLATIONS[languageCode] || this.TRANSLATIONS.en;
  }

  /**
   * Get a specific translation text
   */
  static getTranslation(languageCode: string, key: keyof SurveyTranslations): string {
    const translations = this.getTranslations(languageCode);
    return translations[key] || this.TRANSLATIONS.en[key];
  }

  /**
   * Get flag URL for a language
   */
  static getFlagUrl(languageCode: string, size: 20 | 40 | 80 | 160 = 40): string {
    const language = this.getLanguage(languageCode);
    if (!language) return '';

    return `https://flagcdn.com/w${size}/${language.countryCode}.png`;
  }

  /**
   * Format page navigation text (e.g., "Page 1 of 5")
   */
  static formatPageOf(languageCode: string, current: number, total: number): string {
    const template = this.getTranslation(languageCode, 'pageOf');
    return template
      .replace('{current}', current.toString())
      .replace('{total}', total.toString());
  }

  /**
   * Check if a language code is supported
   */
  static isLanguageSupported(languageCode: string): boolean {
    return this.LANGUAGES.some(lang => lang.code === languageCode);
  }

  /**
   * Returns a localized label for the generic word "Survey".
   * Used for fallbacks and document/tab titles.
   */
  static getSurveyLabel(languageCode?: string): string {
    const normalized = (languageCode || 'en').toLowerCase();
    const candidates = normalized.includes('-')
      ? [normalized, normalized.split('-')[0]]
      : [normalized];

    for (const candidate of candidates) {
      if (this.SURVEY_LABELS[candidate]) {
        return this.SURVEY_LABELS[candidate];
      }
    }

    // As a last resort, try matching any survey label whose base locale matches
    const base = candidates[candidates.length - 1];
    const entry = Object.entries(this.SURVEY_LABELS).find(([key]) => key.split('-')[0] === base);
    if (entry) {
      return entry[1];
    }

    return this.SURVEY_LABELS.en;
  }

  /**
   * Get languages by region (returns languages commonly spoken in a region)
   */
  static getLanguagesByRegion(region: 'europe' | 'asia' | 'americas' | 'middleeast' | 'africa'): Language[] {
    const regionMap: Record<string, string[]> = {
      europe: ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'sv', 'da', 'no', 'fi', 'el', 'cs', 'hu', 'ro', 'uk'],
      asia: ['ja', 'zh', 'zh-TW', 'ko', 'hi', 'th', 'vi', 'id', 'ms'],
      americas: ['en', 'es', 'pt', 'fr'],
      middleeast: ['ar', 'tr', 'he'],
      africa: ['en', 'fr', 'ar', 'pt']
    };

    const codes = regionMap[region] || [];
    return this.LANGUAGES.filter(lang => codes.includes(lang.code));
  }

  /**
   * Get extended translation text (includes validation messages, placeholders, etc.)
   * This method accesses translations from the TRANSLATIONS object
   */
  static getExtendedTranslation(languageCode: string, key: keyof SurveyTranslations): string {
    const translations = this.getTranslations(languageCode);
    return translations[key] || this.TRANSLATIONS.en[key];
  }

  /**
   * Helper to get the correct plural form for "option(s)"
   */
  static getOptionText(languageCode: string, count: number): string {
    const translations = this.getTranslations(languageCode);
    return count === 1 ? translations.option : translations.options;
  }
}

export default LanguageHelper;
